/**
 * Fixed-step simulation loop, decoupled from render (spec §1).
 *
 * Simulation runs at a fixed 60 Hz so physics and game feel are deterministic
 * and frame-rate independent. Render runs as fast as the browser will let it and
 * receives an interpolation alpha so motion stays smooth between sim ticks.
 *
 * Deliberately has no knowledge of Three.js, Rapier, or the game — it is a clock.
 */

export const SIM_HZ = 60;
export const FIXED_DT = 1 / SIM_HZ;

/** Never simulate more than this many steps in one frame (spiral-of-death guard). */
const MAX_STEPS_PER_FRAME = 5;

export interface LoopCallbacks {
  /** Advance the simulation by exactly `FIXED_DT` seconds. */
  fixedUpdate(dt: number): void;
  /**
   * Draw. `alpha` is 0..1 through the current sim step — use it to interpolate
   * rendered transforms between the previous and current simulation state.
   */
  render(alpha: number, frameDt: number): void;
}

export interface LoopHandle {
  stop(): void;
  /**
   * Stop RAF from advancing fixed updates while rendering continues.
   *
   * This is deliberately a loop control rather than a gameplay pause: callers
   * can drive the same fixed-update callback manually without making the game
   * believe its own `paused` state changed.
   */
  pauseFixedUpdates(): void;
  /**
   * Run an exact number of fixed updates while RAF advancement is paused.
   *
   * Refusing to race RAF is important here. A stepping seam that permits both
   * paths at once cannot honestly say which input snapshot produced a tick.
   */
  stepFixedUpdates(steps: number): void;
  /** Resume RAF-driven fixed updates without paying back paused wall time. */
  resumeFixedUpdates(): void;
  /**
   * Throw away any accumulated backlog without running it.
   *
   * For resuming from a pause. The loop keeps accumulating wall-clock time while
   * the game gates its own `fixedUpdate` work, so a resume otherwise *owes* that
   * time and pays it back at up to `MAX_STEPS_PER_FRAME` in one frame — measured
   * at exactly 0.1 s of catch-up on the first frame after unpausing, which is a
   * visible jump. Pausing is not falling behind, so there is no debt to pay.
   */
  dropBacklog(): void;
  /** Wall-clock seconds since start(), simulation time only. */
  readonly simTime: number;
  readonly frameCount: number;
}

export function startLoop(callbacks: LoopCallbacks): LoopHandle {
  let running = true;
  let rafId = 0;
  // Exempt from the simulation guard: the loop is the one place allowed to
  // know what time it is, because it turns wall-clock frames into fixed steps.
  let last = performance.now() / 1000;
  let accumulator = 0;
  let simTime = 0;
  let frameCount = 0;
  let fixedUpdatesPaused = false;

  const frame = (nowMs: number): void => {
    if (!running) return;
    rafId = requestAnimationFrame(frame);

    const now = nowMs / 1000;
    let frameDt = now - last;
    last = now;

    // A tab that was backgrounded can hand us an enormous delta. Clamp rather
    // than trying to catch up — catching up is how you get a physics explosion.
    if (frameDt > 0.25) frameDt = 0.25;

    if (fixedUpdatesPaused) {
      // This is a test seam, not a slow frame. It must discard wall time while
      // stopped, or resuming would covertly add RAF-owned ticks after the
      // caller's explicitly counted manual sequence.
      accumulator = 0;
    } else {
      accumulator += frameDt;

      let steps = 0;
      while (accumulator >= FIXED_DT && steps < MAX_STEPS_PER_FRAME) {
        callbacks.fixedUpdate(FIXED_DT);
        accumulator -= FIXED_DT;
        simTime += FIXED_DT;
        steps++;
      }

      // If we hit the step cap we are running behind; drop the backlog instead of
      // accumulating debt we will never pay off.
      if (steps === MAX_STEPS_PER_FRAME) accumulator = 0;
    }

    callbacks.render(accumulator / FIXED_DT, frameDt);
    frameCount++;
  };

  rafId = requestAnimationFrame(frame);

  return {
    stop(): void {
      running = false;
      cancelAnimationFrame(rafId);
    },
    pauseFixedUpdates(): void {
      fixedUpdatesPaused = true;
      accumulator = 0;
      last = performance.now() / 1000;
    },
    stepFixedUpdates(steps: number): void {
      if (!fixedUpdatesPaused) {
        throw new Error('stepFixedUpdates() requires pauseFixedUpdates() first');
      }
      if (!Number.isSafeInteger(steps) || steps < 0) {
        throw new Error(`stepFixedUpdates() requires a non-negative safe integer, got ${String(steps)}`);
      }
      for (let step = 0; step < steps; step += 1) {
        callbacks.fixedUpdate(FIXED_DT);
        simTime += FIXED_DT;
      }
    },
    resumeFixedUpdates(): void {
      fixedUpdatesPaused = false;
      accumulator = 0;
      last = performance.now() / 1000;
    },
    dropBacklog(): void {
      accumulator = 0;
      // `last` too, or the very next frame's delta still spans the whole pause
      // and immediately rebuilds the backlog that was just discarded.
      // This clock read shares the loop's exemption: it resets the frame driver,
      // rather than introducing wall-clock time into simulation state.
      last = performance.now() / 1000;
    },
    get simTime(): number {
      return simTime;
    },
    get frameCount(): number {
      return frameCount;
    },
  };
}
